class MaryssilSizeGuide extends HTMLElement {
  #dialog;
  #closeButton;
  #openers = [];
  #lastFocusedElement = null;
  #isOpen = false;

  connectedCallback() {
    if (this.#dialog) return;

    const dialog = this.querySelector('[data-maryssil-size-guide-dialog]');
    if (!(dialog instanceof HTMLDialogElement)) return;

    this.#dialog = dialog;
    this.#closeButton = dialog.querySelector('[data-maryssil-size-guide-close]');
    this.#openers = [...document.querySelectorAll('[data-maryssil-size-guide-open]')].filter(
      (opener) => opener instanceof HTMLElement && opener.getAttribute('aria-controls') === dialog.id,
    );

    this.#openers.forEach((opener) => {
      opener.addEventListener('click', this.#handleOpen);
      opener.setAttribute('aria-expanded', 'false');
    });
    this.#closeButton?.addEventListener('click', this.#handleClose);
    dialog.addEventListener('cancel', this.#handleCancel);
    dialog.addEventListener('click', this.#handleDialogClick);
    dialog.addEventListener('keydown', this.#handleDialogKeydown);
    dialog.addEventListener('close', this.#handleDialogClose);
  }

  disconnectedCallback() {
    this.#openers.forEach((opener) => opener.removeEventListener('click', this.#handleOpen));
    this.#closeButton?.removeEventListener('click', this.#handleClose);
    this.#dialog?.removeEventListener('cancel', this.#handleCancel);
    this.#dialog?.removeEventListener('click', this.#handleDialogClick);
    this.#dialog?.removeEventListener('keydown', this.#handleDialogKeydown);
    this.#dialog?.removeEventListener('close', this.#handleDialogClose);
    document.documentElement.classList.remove('maryssil-size-guide-open');
    this.#dialog = null;
    this.#closeButton = null;
    this.#openers = [];
    this.#lastFocusedElement = null;
    this.#isOpen = false;
  }

  #handleOpen = (event) => {
    event.preventDefault();
    const opener = event.currentTarget;
    if (!(opener instanceof HTMLElement)) return;

    this.#lastFocusedElement = opener;
    this.#open();
  };

  #handleClose = () => {
    this.#close();
  };

  #handleCancel = (event) => {
    event.preventDefault();
    this.#close();
  };

  #handleDialogClick = (event) => {
    if (event.target === this.#dialog) this.#close();
  };

  #handleDialogKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.#close();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = this.#getFocusableElements();
    if (focusableElements.length === 0) {
      event.preventDefault();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  #handleDialogClose = () => {
    this.#syncClosedState();
  };

  #open() {
    if (!this.#dialog || this.#isOpen) return;

    this.#isOpen = true;
    if (typeof this.#dialog.showModal === 'function') {
      this.#dialog.showModal();
    } else {
      this.#dialog.setAttribute('open', '');
    }

    document.documentElement.classList.add('maryssil-size-guide-open');
    this.#openers.forEach((opener) => opener.setAttribute('aria-expanded', 'true'));
    requestAnimationFrame(() => this.#closeButton?.focus({ preventScroll: true }));
  }

  #close() {
    if (!this.#dialog || !this.#isOpen) return;

    if (this.#dialog.open && typeof this.#dialog.close === 'function') {
      this.#dialog.close();
    } else {
      this.#dialog.removeAttribute('open');
      this.#syncClosedState();
    }
  }

  #syncClosedState() {
    if (!this.#isOpen) return;

    this.#isOpen = false;
    document.documentElement.classList.remove('maryssil-size-guide-open');
    this.#openers.forEach((opener) => opener.setAttribute('aria-expanded', 'false'));

    const focusTarget = this.#lastFocusedElement;
    this.#lastFocusedElement = null;
    if (focusTarget?.isConnected) focusTarget.focus({ preventScroll: true });
  }

  #getFocusableElements() {
    if (!this.#dialog) return [];

    return [...this.#dialog.querySelectorAll(
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )].filter((element) => element instanceof HTMLElement && !element.hidden);
  }
}

if (!customElements.get('maryssil-size-guide')) {
  customElements.define('maryssil-size-guide', MaryssilSizeGuide);
}
